import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';

const LoadingAppDetail: React.FC = () => {
  const navigate = useNavigate();
  const [loadingTime, setLoadingTime] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setLoadingTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getLoadingMessage = () => {
    if (loadingTime < 3) return 'Loading app details...';
    if (loadingTime < 8) return 'Fetching app information...';
    return 'This is taking longer than usual. Please wait...';
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 bg-background/80 backdrop-blur-sm border-b border-border z-10 safe-area-top">
        <div className="p-4">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="flex items-center space-x-2 active:scale-[0.97] transition-transform"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span>Back</span>
            </Button>
          </div>
          
          {/* Loading App Info */}
          <div role="status" aria-live="polite" aria-label="Loading app details">
            <div className="flex items-center space-x-4 mb-4">
              <Skeleton className="w-16 h-16 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        </div>
      </div>

      {/* Loading Description */}
      <div className="p-4">
        <div className="bg-muted rounded-lg h-96 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" aria-hidden="true"></div>
            <p className="text-muted-foreground">{getLoadingMessage()}</p>
            {loadingTime > 5 && (
              <p className="text-sm text-muted-foreground mt-2">
                Elapsed time: {loadingTime}s
              </p>
            )}
            <span className="sr-only">Loading app details, please wait</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingAppDetail;
